import React from "react";
import "./FilterControl.scss";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Divider, Input, Row, Slider } from "antd";
import { useNavigate } from "react-router-dom";
import Validates from "@utils/Validates";
import { IProductSearch } from "@utils/Interfaces";
import { isArray } from "lodash";

interface IFilterControl {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  searchParam: IProductSearch;
  setSearchParam: (filters: IProductSearch) => void;
  submit?: () => void;
  componentIn: string;
}

const FilterControl: React.FC<IFilterControl> = ({
  isOpen,
  setIsOpen,
  searchParam,
  setSearchParam,
  submit,
  componentIn,
}) => {
  const navigate = useNavigate();

  const handleSetFilterParam = (key: string, value: any) => {
    if (
      key === "price_min" ||
      key === "price_max" ||
      key === "bale_weight_min" ||
      key === "bale_weight_max"
    ) {
      value = Validates.validateNumberForm(value);
    }
    setSearchParam({
      ...searchParam,
      filters: { ...searchParam.filters, [key]: value },
    });
  };

  const handleSumorizeArray = (isChecked: boolean, key: string, value: any) => {
    let tmpArray = [...searchParam.filters[key]];
    if (isChecked) {
      tmpArray.push(value);
    } else {
      tmpArray = tmpArray.filter((item: any) => item !== value);
    }
    handleSetFilterParam(key, tmpArray);
  };

  const handleApply = () => {
    if (componentIn === "productCatalog") {
      const encodedParams = encodeURIComponent(JSON.stringify(searchParam));

      navigate(`/search?params=${encodedParams}`);
    } else {
      submit?.();
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    setSearchParam({
      ...searchParam,
      filters: {
        hay_cut: [],
        bale_weight_min: 0,
        bale_weight_max: 200,
        shape: [],
        size: [],
        hay_compression: [],
        moisture: [],
        rain: [],
        storage: [],
        crop: [],
        binding_type: [],
        price_min: 0,
        price_max: 1000,
        shipping_options: [],
        payment_options: [],
        crude_protein_range_ids: [],
      },
    });
  };

  return (
    <>
      {isOpen && <div className="overlay" onClick={() => setIsOpen(false)} />}
      <div className={`FilterControl ${isOpen ? "open" : ""}`}>
        <div className="filter-content">
          <div className="control-header-wrap">
            <div className="control-header">
              <div className="title">Filter</div>
              <div className="close-control">
                <Button
                  icon={<CloseOutlined />}
                  variant="link"
                  color="default"
                  onClick={() => setIsOpen(false)}
                />
              </div>
            </div>
          </div>
          <div className="control-body">
            <div className="filter-group">
              <div className="title">Hay Cut</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.hay_cut.includes("1st cut")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_cut",
                        "1st cut"
                      )
                    }
                  >
                    1st Cut
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.hay_cut.includes("2nd cut")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_cut",
                        "2nd cut"
                      )
                    }
                  >
                    2nd Cut
                  </Checkbox>
                </Col>
                <Col span={7}>
                  <Checkbox
                    checked={searchParam.filters.hay_cut.includes("3rd cut")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_cut",
                        "3rd cut"
                      )
                    }
                  >
                    3rd Cut
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Bale Weight</div>
              <Slider
                range
                max={200}
                value={[
                  searchParam.filters.bale_weight_min,
                  searchParam.filters.bale_weight_max,
                ]}
                onChange={(values) => {
                  setSearchParam({
                    ...searchParam,
                    filters: {
                      ...searchParam.filters,
                      bale_weight_min: values[0],
                      bale_weight_max: values[1],
                    },
                  });
                }}
                className="bale-weight-slider"
              />
              <div className="baleweight-input-groups">
                <Input
                  className="input-number"
                  size="large"
                  value={searchParam.filters.bale_weight_min}
                  onChange={(e) => {
                    handleSetFilterParam("bale_weight_min", e.target.value);
                  }}
                  variant="filled"
                  suffix="ton"
                />
                <span>to</span>
                <Input
                  className="input-number"
                  size="large"
                  value={searchParam.filters.bale_weight_max}
                  variant="filled"
                  onChange={(e) => {
                    handleSetFilterParam("bale_weight_max", e.target.value);
                  }}
                  suffix="ton"
                />
              </div>
            </div>
            <div className="filter-group">
              <div className="title">Shape, Size</div>
              <div className="size-group">
                <Row gutter={[14, 14]} align={"middle"} justify={"center"}>
                  <Col span={24}>
                    <Checkbox
                      checked={searchParam.filters.shape.includes(4)}
                      onChange={(e) =>
                        handleSumorizeArray(e.target.checked, "shape", 4)
                      }
                    >
                      Small Square
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox
                      checked={searchParam.filters.shape.includes(5)}
                      onChange={(e) =>
                        handleSumorizeArray(e.target.checked, "shape", 5)
                      }
                    >
                      Large Square
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Checkbox
                      checked={searchParam.filters.shape.includes(2)}
                      onChange={(e) =>
                        handleSumorizeArray(e.target.checked, "shape", 2)
                      }
                    >
                      Round
                    </Checkbox>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="filter-group">
              <div className="title">Hay Compression (Baling)</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.hay_compression.includes(
                      "loose"
                    )}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_compression",
                        "loose"
                      )
                    }
                  >
                    Loose
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.hay_compression.includes(
                      "medium"
                    )}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_compression",
                        "medium"
                      )
                    }
                  >
                    Medium
                  </Checkbox>
                </Col>
                <Col span={7}>
                  <Checkbox
                    checked={searchParam.filters.hay_compression.includes(
                      "high"
                    )}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "hay_compression",
                        "high"
                      )
                    }
                  >
                    High
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Moisture</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.moisture.includes(0)}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "moisture", 0)
                    }
                  >{`< 15%`}</Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.moisture.includes(1)}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "moisture", 1)
                    }
                  >
                    15% - 20%
                  </Checkbox>
                </Col>
                <Col span={7}>
                  <Checkbox
                    checked={searchParam.filters.moisture.includes(2)}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "moisture", 2)
                    }
                  >{`> 20%`}</Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Rain</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.rain.includes("no")}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "rain", "no")
                    }
                  >
                    No
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.rain.includes("yes")}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "rain", "yes")
                    }
                  >
                    Yes
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Storage</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.storage.includes("Covered")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "storage",
                        "Covered"
                      )
                    }
                  >
                    Covered
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.storage.includes("Open")}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "storage", "Open")
                    }
                  >
                    Open
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Crop</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.crop.includes("Old")}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "crop", "Old")
                    }
                  >
                    Old
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.crop.includes("New")}
                    onChange={(e) =>
                      handleSumorizeArray(e.target.checked, "crop", "New")
                    }
                  >
                    New
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Type of Binding</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    checked={searchParam.filters.binding_type.includes("Net")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "binding_type",
                        "Net"
                      )
                    }
                  >
                    Net
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    checked={searchParam.filters.binding_type.includes("Twine")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "binding_type",
                        "Twine"
                      )
                    }
                  >
                    Twine
                  </Checkbox>
                </Col>
                <Col span={7}>
                  <Checkbox
                    checked={searchParam.filters.binding_type.includes("Wrap")}
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "binding_type",
                        "Wrap"
                      )
                    }
                  >
                    Wrap
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <div className="filter-group">
              <div className="title">Crude Protein (CP)</div>
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <Checkbox
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "crude_protein_range_ids",
                        0
                      )
                    }
                    checked={searchParam.filters.crude_protein_range_ids.includes(
                      0
                    )}
                  >
                    No
                  </Checkbox>
                </Col>
                <Col span={9}>
                  <Checkbox
                    onChange={(e) =>
                      handleSumorizeArray(
                        e.target.checked,
                        "crude_protein_range_ids",
                        1
                      )
                    }
                    checked={searchParam.filters.crude_protein_range_ids.includes(
                      1
                    )}
                  >
                    Yes
                  </Checkbox>
                </Col>
              </Row>
            </div>
            <Divider />
            <div className="filter-group">
              <div className="title">Price per Weight ($/ton)</div>
              <Slider
                range
                max={1000}
                onChange={(values) => {
                  setSearchParam({
                    ...searchParam,
                    filters: {
                      ...searchParam.filters,
                      price_min: values[0],
                      price_max: values[1],
                    },
                  });
                }}
                value={[
                  searchParam.filters.price_min,
                  searchParam.filters.price_max,
                ]}
                className="bale-weight-slider"
              />
              <div className="baleweight-input-groups">
                <Input
                  className="input-number"
                  value={searchParam.filters.price_min}
                  onChange={(e) => {
                    handleSetFilterParam("price_min", e.target.value);
                  }}
                  size="large"
                  variant="filled"
                  prefix={"$"}
                />
                <span>to</span>
                <Input
                  className="input-number"
                  value={searchParam.filters.price_max}
                  onChange={(e) => {
                    handleSetFilterParam("price_max", e.target.value);
                  }}
                  size="large"
                  variant="filled"
                  prefix={"$"}
                />
              </div>
            </div>
            <Divider />
            <div className="filter-group">
              <div className="title">Shipping Options</div>
              <div className="checkbox-group">
                <Checkbox
                  checked={searchParam.filters.shipping_options.includes(
                    "Consumer Pick-up"
                  )}
                  onChange={(e) =>
                    handleSumorizeArray(
                      e.target.checked,
                      "shipping_options",
                      "Consumer Pick-up"
                    )
                  }
                >
                  Pick-up
                </Checkbox>
                <Checkbox
                  checked={searchParam.filters.shipping_options.includes(
                    "Shipping by Producer"
                  )}
                  onChange={(e) =>
                    handleSumorizeArray(
                      e.target.checked,
                      "shipping_options",
                      "Shipping by Producer"
                    )
                  }
                >
                  Delivered
                </Checkbox>
              </div>
            </div>
            <div className="filter-group">
              <div className="title">Payment Methods</div>
              <div className="checkbox-group">
                <Checkbox
                  checked={searchParam.filters.payment_options.includes("Card")}
                  onChange={(e) =>
                    handleSumorizeArray(
                      e.target.checked,
                      "payment_options",
                      "Card"
                    )
                  }
                >
                  Debit/Credit Card
                </Checkbox>
                <Checkbox
                  checked={searchParam.filters.payment_options.includes(
                    "Paypal"
                  )}
                  onChange={(e) =>
                    handleSumorizeArray(
                      e.target.checked,
                      "payment_options",
                      "Paypal"
                    )
                  }
                >
                  Paypal
                </Checkbox>
                {/* <Checkbox>Interact e-Transfer</Checkbox> */}
                {/* <Checkbox>Escrow</Checkbox> */}
                {/* <Checkbox>Cash</Checkbox> */}
              </div>
            </div>
          </div>
          <div className="control-action">
            <Button className="reset-btn" onClick={handleReset}>
              Reset
            </Button>
            <Button className="apply-btn" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterControl;
